import React, { useState, useMemo } from 'react';
import {
  CoupleSettings,
  MemoryItem,
  DailyLetter,
  VisitorLog,
  MyWebRequest,
} from '../../types';
import { AdminSection } from '../AdminPage';
import {
  ShieldCheck,
  Mail,
  ImageIcon,
  Globe,
  Sparkles,
  Music,
  Droplets,
  Flower2,
  Sun,
  Smartphone,
  Wrench,
  Users,
  Database,
  Layers,
  Search,
  X,
  Heart,
  Shield,
  ChevronRight,
  Quote,
  BarChart3,
} from 'lucide-react';
import { calculateHydrationState } from '../../utils/wateringUtils';
import { calculateTimeElapsed } from '../../utils/storage';

interface ModernAdminDashboardProps {
  settings: CoupleSettings;
  memories: MemoryItem[];
  lettersList: DailyLetter[];
  visitorLogs: VisitorLog[];
  webRequests: MyWebRequest[];
  activeProfile: 'novio' | 'novia';
  p1Name: string;
  p2Name: string;
  p1Avatar: string;
  p2Avatar: string;
  onSelectSection: (section: AdminSection) => void;
  onOpenUploadModal: () => void;
  onNavigateToPublic: (route?: string) => void;
  onSaveSettings: (newSettings: CoupleSettings) => void;
  onEditMemory: (mem: MemoryItem) => void;
  triggerToast: (msg: string) => void;
  isUserWeb?: boolean;
}

export const ModernAdminDashboard: React.FC<ModernAdminDashboardProps> = ({
  settings,
  activeProfile,
  p1Name,
  p2Name,
  p1Avatar,
  p2Avatar,
  onSelectSection,
  isUserWeb,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Hydration state of botanical flowers
  const hydration = useMemo(() => {
    return calculateHydrationState(
      settings.gardenWatering?.lastWateredAt,
      settings.gardenWatering?.wateringIntervalDays || 3
    );
  }, [settings.gardenWatering]);

  const timeElapsedData = useMemo(() => {
    if (isUserWeb && !settings.startDate) {
      return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0, totalDays: 0 };
    }
    return calculateTimeElapsed(settings.startDate || '2024-01-01');
  }, [settings.startDate, isUserWeb]);

  // Modules List definition
  const allModules: Array<{
    id: AdminSection;
    title: string;
    desc: string;
    icon: React.ReactNode;
    badge: string;
    badgeColor: 'emerald' | 'rose' | 'amber' | 'blue' | 'slate' | 'violet';
    category: 'romance' | 'garden' | 'system';
  }> = [
    // Contenido & Romance
    {
      id: 'memories',
      title: 'Álbum & Recuerdos',
      desc: 'Fotos, carruseles, videos y fechas especiales',
      icon: <ImageIcon className="w-5 h-5 text-amber-500" />,
      badge: 'Galería',
      badgeColor: 'amber',
      category: 'romance',
    },
    {
      id: 'letters',
      title: 'Cartas de Amor',
      desc: 'Cartas secretas, sellos de cera y bóveda',
      icon: <Mail className="w-5 h-5 text-rose-500" />,
      badge: 'Bóveda privada',
      badgeColor: 'rose',
      category: 'romance',
    },
    {
      id: 'music',
      title: 'Música & Playlist',
      desc: 'Banda sonora romántica de fondo y pistas',
      icon: <Music className="w-5 h-5 text-violet-500" />,
      badge: 'Banda sonora',
      badgeColor: 'violet',
      category: 'romance',
    },
    {
      id: 'phrases',
      title: 'Frases & Dedicatorias',
      desc: 'Frase principal de portada y apodos de cariño',
      icon: <Quote className="w-5 h-5 text-pink-500" />,
      badge: 'Personalizable',
      badgeColor: 'rose',
      category: 'romance',
    },
    {
      id: 'couple',
      title: 'Identidad de Pareja',
      desc: 'Nombres, fotos de perfil, cumpleaños y sangre',
      icon: <Users className="w-5 h-5 text-rose-500" />,
      badge: 'Configuración',
      badgeColor: 'rose',
      category: 'romance',
    },

    // Jardín Interactivo
    {
      id: 'tulips',
      title: 'Color de Tulipanes',
      desc: 'Colores unificados o individuales para las flores',
      icon: <Flower2 className="w-5 h-5 text-rose-500" />,
      badge: settings.tulipSettings?.mode === 'all' ? 'Unificados' : 'Personalizados',
      badgeColor: 'rose',
      category: 'garden',
    },
    {
      id: 'watering',
      title: 'Riego & Cuidado Jardín',
      desc: 'Nivel de agua de la regadera e hidratación',
      icon: <Droplets className="w-5 h-5 text-sky-500" />,
      badge: hydration.stageLabel,
      badgeColor: hydration.hydrationPercent > 50 ? 'emerald' : 'amber',
      category: 'garden',
    },
    {
      id: 'atmosphere',
      title: 'Ambiente & Cielo',
      desc: 'Ciclo dinámico solar, luna, lluvia y estrellas',
      icon: <Sun className="w-5 h-5 text-amber-500" />,
      badge: (settings.atmosphereMode || 'auto') === 'auto' ? 'Automático' : 'Manual',
      badgeColor: 'blue',
      category: 'garden',
    },

    // Plataforma & Seguridad
    {
      id: 'visitors',
      title: 'Estadísticas de la web',
      desc: 'Consulta el tráfico e impacto en tiempo real',
      icon: <BarChart3 className="w-5 h-5 text-teal-400" />,
      badge: 'En vivo',
      badgeColor: 'emerald',
      category: 'system',
    },
    {
      id: 'app',
      title: 'App Móvil & Descarga',
      desc: 'Instalador PWA, código QR y enlaces APK/iOS',
      icon: <Smartphone className="w-5 h-5 text-indigo-500" />,
      badge: 'Instalable',
      badgeColor: 'blue',
      category: 'system',
    },
    {
      id: 'web_settings',
      title: 'Ajustes Web',
      desc: 'Mantenimiento del sitio y opciones generales',
      icon: <Wrench className="w-5 h-5 text-slate-600" />,
      badge: settings.webMaintenance?.isEnabled ? 'En Mantenimiento' : 'Operativa',
      badgeColor: settings.webMaintenance?.isEnabled ? 'rose' : 'emerald',
      category: 'system',
    },
    {
      id: 'myweb',
      title: 'Solicitudes "Mi Web"',
      desc: 'Peticiones de parejas para crear su propio sitio',
      icon: <Sparkles className="w-5 h-5 text-emerald-500" />,
      badge: 'Plantillas Web',
      badgeColor: 'emerald',
      category: 'system',
    },
    {
      id: 'security',
      title: 'Seguridad & PIN',
      desc: 'Clave maestra de acceso al panel administrativo',
      icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />,
      badge: 'Protegido',
      badgeColor: 'emerald',
      category: 'system',
    },
    {
      id: 'database',
      title: 'Base de Datos & Nube',
      desc: 'Copia de seguridad y sincronización Firestore',
      icon: <Database className="w-5 h-5 text-blue-500" />,
      badge: 'Sincronizado',
      badgeColor: 'blue',
      category: 'system',
    },
    {
      id: 'navigation',
      title: 'Menú & Visibilidad',
      desc: 'Activar u ocultar pestañas en la web pública',
      icon: <Layers className="w-5 h-5 text-purple-500" />,
      badge: 'Personalizable',
      badgeColor: 'slate',
      category: 'system',
    },
  ];

  // In custom/personalized webs, platform and security modules are strictly excluded
  const baseModules = isUserWeb
    ? allModules.filter((m) => m.category !== 'system')
    : allModules;

  const searchFilter = searchQuery.trim().toLowerCase();
  const filteredModules = searchFilter
    ? baseModules.filter(
        (m) =>
          m.title.toLowerCase().includes(searchFilter) ||
          m.desc.toLowerCase().includes(searchFilter) ||
          m.badge.toLowerCase().includes(searchFilter)
      )
    : baseModules;

  const romanceModules = filteredModules.filter((m) => m.category === 'romance');
  const gardenModules = filteredModules.filter((m) => m.category === 'garden');
  const systemModules = filteredModules.filter((m) => m.category === 'system');

  const getBadgeClasses = (color: 'emerald' | 'rose' | 'amber' | 'blue' | 'slate' | 'violet') => {
    switch (color) {
      case 'emerald':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'rose':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'amber':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'blue':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'violet':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Sleek Modern Hero Header */}
      <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-7 shadow-xs relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-rose-500 via-amber-400 to-rose-400" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-rose-500" />
                <span>Panel de Control</span>
              </span>

              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>En vivo</span>
              </div>

              <span className="text-xs text-slate-500 font-medium">
                {timeElapsedData.years > 0 ? `${timeElapsedData.years}a ` : ''}
                {timeElapsedData.months}m {timeElapsedData.days}d juntos
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100 shadow-xs flex items-center justify-center">
                {(activeProfile === 'novio' ? p1Avatar : p2Avatar) ? (
                  <img
                    src={activeProfile === 'novio' ? p1Avatar : p2Avatar}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className={`w-full h-full flex items-center justify-center font-bold text-base select-none ${
                      activeProfile === 'novio'
                        ? 'bg-gradient-to-br from-sky-400 to-blue-600 text-white'
                        : 'bg-gradient-to-br from-rose-400 to-pink-600 text-white'
                    }`}
                  >
                    {((activeProfile === 'novio' ? p1Name : p2Name) || (activeProfile === 'novio' ? 'N' : 'E')).charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <h1 className="font-['Playfair_Display',serif] text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                  Hola, {activeProfile === 'novio' ? p1Name || 'Novio' : p2Name || 'Novia'} {activeProfile === 'novio' ? '💙' : '💖'}
                </h1>
                <p className="text-xs text-slate-500 font-medium">
                  Gestiona tu espacio de recuerdos, cartas, jardín y privacidad
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Quick Module Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-2xl border border-slate-200/90 shadow-xs">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar módulo, ajuste o herramienta..."
            className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 focus:bg-white transition-all placeholder:text-slate-400 font-medium"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold shrink-0 px-1">
          <span>{filteredModules.length} módulos disponibles</span>
        </div>
      </div>

      {/* 5. Organized Modules Grids */}
      <div className="space-y-6">
        {/* Category 1: Contenido & Romance */}
        {romanceModules.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Heart className="w-4 h-4 text-rose-500" />
              <h2 className="text-xs uppercase font-extrabold tracking-wider text-slate-500">
                Contenido & Romance
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {romanceModules.map((mod) => (
                <button
                  key={mod.id}
                  type="button"
                  onClick={() => onSelectSection(mod.id)}
                  className="p-4 rounded-2xl bg-white border border-slate-200/90 hover:border-slate-300 hover:shadow-xs transition-all text-left flex items-start justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      {mod.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-rose-600 transition-colors">
                        {mod.title}
                      </h3>
                      <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{mod.desc}</p>
                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border mt-2 ${getBadgeClasses(mod.badgeColor)}`}>
                        {mod.badge}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0 mt-2.5" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category 2: Jardín Interactivo */}
        {gardenModules.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Flower2 className="w-4 h-4 text-emerald-500" />
              <h2 className="text-xs uppercase font-extrabold tracking-wider text-slate-500">
                Jardín Interactivo
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {gardenModules.map((mod) => (
                <button
                  key={mod.id}
                  type="button"
                  onClick={() => onSelectSection(mod.id)}
                  className="p-4 rounded-2xl bg-white border border-slate-200/90 hover:border-slate-300 hover:shadow-xs transition-all text-left flex items-start justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      {mod.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-rose-600 transition-colors">
                        {mod.title}
                      </h3>
                      <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{mod.desc}</p>
                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border mt-2 ${getBadgeClasses(mod.badgeColor)}`}>
                        {mod.badge}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0 mt-2.5" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category 3: Plataforma & Seguridad */}
        {!isUserWeb && systemModules.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Shield className="w-4 h-4 text-slate-600" />
              <h2 className="text-xs uppercase font-extrabold tracking-wider text-slate-500">
                Plataforma & Seguridad
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {systemModules.map((mod) => (
                <button
                  key={mod.id}
                  type="button"
                  onClick={() => onSelectSection(mod.id)}
                  className="p-4 rounded-2xl bg-white border border-slate-200/90 hover:border-slate-300 hover:shadow-xs transition-all text-left flex items-start justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      {mod.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold text-slate-900 group-hover:text-rose-600 transition-colors">
                        {mod.title}
                      </h3>
                      <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{mod.desc}</p>
                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border mt-2 ${getBadgeClasses(mod.badgeColor)}`}>
                        {mod.badge}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0 mt-2.5" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
